// ==================== BANK RECONCILIATION SERVICE ====================
// Extracted from database.js - contains all bank reconciliation functionality

import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../utils/database/firebase-config';
// NOTE: Service dependencies commented out to avoid circular imports during refactoring
// import { journalEntriesService } from '../accounting/journal-entries';

export const bankReconciliationService = {
  async getBankAccounts(companyId) {
    const accountsRef = collection(db, 'bankAccounts');
    const q = query(accountsRef, where('companyId', '==', companyId), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async addBankAccount(accountData) {
    const accountsRef = collection(db, 'bankAccounts');
    const docRef = await addDoc(accountsRef, {
      ...accountData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isActive: true,
      lastReconciled: null,
      currentBalance: 0
    });
    return docRef.id;
  },

  async updateBankAccount(accountId, accountData) {
    const accountRef = doc(db, 'bankAccounts', accountId);
    await updateDoc(accountRef, {
      ...accountData,
      updatedAt: serverTimestamp()
    });
    return true;
  },

  async getBankStatements(companyId, bankAccountId = null) {
    const statementsRef = collection(db, 'bankStatements');
    let q;
    if (bankAccountId) {
      q = query(statementsRef, 
        where('companyId', '==', companyId),
        where('bankAccountId', '==', bankAccountId),
        orderBy('statementDate', 'desc')
      );
    } else {
      q = query(statementsRef, 
        where('companyId', '==', companyId),
        orderBy('statementDate', 'desc')
      );
    }
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async uploadBankStatement(statementData) {
    const statementsRef = collection(db, 'bankStatements');
    const docRef = await addDoc(statementsRef, {
      ...statementData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      reconciliationStatus: 'pending',
      matchedTransactions: 0,
      unmatchedTransactions: statementData.transactions?.length || 0
    });
    return docRef.id;
  },

  async getReconciliations(companyId, bankAccountId = null) {
    const reconciliationsRef = collection(db, 'bankReconciliations');
    let q;
    if (bankAccountId) {
      q = query(reconciliationsRef,
        where('companyId', '==', companyId),
        where('bankAccountId', '==', bankAccountId),
        orderBy('reconciliationDate', 'desc')
      );
    } else {
      q = query(reconciliationsRef,
        where('companyId', '==', companyId),
        orderBy('reconciliationDate', 'desc')
      );
    }
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async createReconciliation(reconciliationData) {
    const reconciliationsRef = collection(db, 'bankReconciliations');
    const docRef = await addDoc(reconciliationsRef, {
      ...reconciliationData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'in_progress'
    });
    return docRef.id;
  },

  async matchTransaction(reconciliationId, bankTransactionId, journalEntryId, matchType = 'exact') {
    const matchesRef = collection(db, 'transactionMatches');
    const docRef = await addDoc(matchesRef, {
      reconciliationId,
      bankTransactionId,
      journalEntryId,
      matchType, // 'exact', 'partial', 'manual'
      matchedAt: serverTimestamp(),
      matchedBy: null, // Will be set by calling function
      isConfirmed: matchType === 'exact'
    });
    return docRef.id;
  },

  async getUnmatchedTransactions(companyId, bankAccountId, startDate, endDate) {
    // Get journal entries for the period
    // TODO: Reconnect with journalEntriesService after refactoring
    // const journalEntries = await journalEntriesService.getJournalEntries(companyId);
    const journalEntries = []; // Temporary placeholder
    const cashEntries = journalEntries.filter(entry => {
      if (entry.status !== 'posted') return false;
      const entryDate = entry.date?.toDate?.() || new Date(entry.date);
      return entryDate >= startDate && entryDate <= endDate &&
        entry.lineItems && entry.lineItems.some(line => line.accountCode === '1000'); // Cash account
    });

    // Get bank statement transactions
    const statements = await this.getBankStatements(companyId, bankAccountId);
    const bankTransactions = statements.flatMap(statement => 
      statement.transactions?.filter(tx => {
        const txDate = tx.date?.toDate?.() || new Date(tx.date);
        return txDate >= startDate && txDate <= endDate;
      }) || []
    );

    // Get existing matches
    const matchesRef = collection(db, 'transactionMatches');
    const matchesSnapshot = await getDocs(matchesRef);
    const existingMatches = matchesSnapshot.docs.map(doc => doc.data());

    // Filter out already matched transactions
    const matchedJournalIds = existingMatches.map(m => m.journalEntryId);
    const matchedBankIds = existingMatches.map(m => m.bankTransactionId);

    return {
      unmatchedJournalEntries: cashEntries.filter(entry => !matchedJournalIds.includes(entry.id)),
      unmatchedBankTransactions: bankTransactions.filter(tx => !matchedBankIds.includes(tx.id))
    };
  },

  async finalizeReconciliation(reconciliationId, reconciledBy) {
    const reconciliationRef = doc(db, 'bankReconciliations', reconciliationId);
    await updateDoc(reconciliationRef, {
      status: 'completed',
      completedAt: serverTimestamp(),
      completedBy: reconciledBy,
      updatedAt: serverTimestamp()
    });
    return true;
  },

  async generateReconciliationReport(reconciliationId) {
    const reconciliationRef = doc(db, 'bankReconciliations', reconciliationId);
    const reconciliation = await getDoc(reconciliationRef);
    
    if (!reconciliation.exists()) {
      throw new Error('Reconciliation not found');
    }

    const data = reconciliation.data();
    
    // Get matched transactions for this reconciliation
    const matchesRef = collection(db, 'transactionMatches');
    const matchesQuery = query(matchesRef, where('reconciliationId', '==', reconciliationId));
    const matchesSnapshot = await getDocs(matchesQuery);
    const matches = matchesSnapshot.docs.map(doc => doc.data());

    return {
      ...data,
      totalMatches: matches.length,
      exactMatches: matches.filter(m => m.matchType === 'exact').length,
      manualMatches: matches.filter(m => m.matchType === 'manual').length,
      matches
    };
  }
}; 